import { LupaIcon } from "assets/icons/LupaIcon";
import { showErrorToast } from "components/CustomAlerts/CustomToast";
import { Input } from "components/Input/Input";
import { Pagination } from "components/Pagination/Pagination";
import { DEFAULT_LIMIT_PAGINATION } from "constants/appConstants";
import { useFormik } from "formik";
import { TituloI } from "interfaces/Titulo";
import { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTitulos } from "services/titulos";
import { AppDispatch, RootState } from "store/store";
import { CajaCurricularI } from "interfaces/CajaCurricular";
import { getCajaCurricular } from "services/cajaCurricular";
import { CajasCurricularesRowActions } from "./CajasCurricularesTable/CajasCurricularesRowActions";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { CajasCurricularesTable } from "./CajasCurricularesTable/CajasCurricularesTable";
import { CajaCurricularDetailModal } from "./CajaCurricularDetailModal/CajaCurricularDetailModal";

export const CajasCurriculares = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [elementos, setElementos] = useState<CajaCurricularI[]>();
  const [cantidad, setCantidad] = useState<number>();
  const [page, setPage] = useState<number>();
  const [limit, setLimit] = useState<number>();

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { tiposNormativa, tiposEfecto, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );

  const [showModalDetail, setShowModalDetail] = useState(false);
  const [selectedElement, setSelectedElement] = useState<
    CajaCurricularI | undefined
  >();

  const initialValues = {
    id: "",
    nombre: "",
    espaciosCurriculares: [],
    planesEstudio: [],
  };

  const { values, handleChange, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {},
  });

  const searchCajasCurriculares = async (params?: {
    filtros?: { [key: string]: any };
  }) => {
    try {
      setIsLoading(true);
      const response = await getCajaCurricular({ ...params?.filtros });
      setElementos(response.elementos);
      setCantidad(response.cantidad);
      setPage(response.page);
      setLimit(params?.filtros?.limit ?? DEFAULT_LIMIT_PAGINATION);
    } catch (err) {
      showErrorToast("No fue posible cargar las cajas curriculares");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModalDetail = () => {
    setShowModalDetail(false);
    setTimeout(() => setSelectedElement(undefined), 500);
  };

  const handleEdit = (elemento: CajaCurricularI) => {
    navigate(PRIVATE_ROUTE.CAJAS_CURRICULARES_EDITAR + "/" + elemento.id);
  };

  const handleVerDetalle = (elemento: CajaCurricularI) => {
    setSelectedElement(elemento);
    setShowModalDetail(true);
  };

  const handleCreate = () => {
    navigate(PRIVATE_ROUTE.CAJAS_CURRICULARES_CREAR);
  };

  const handleChangePagination = (newFiltros: {
    [key: string]: string | number;
  }) => {
    searchCajasCurriculares({ filtros: { ...values, ...newFiltros } });
  };

  const handleClearFilters = () => {
    resetForm();
    searchCajasCurriculares();
  };

  const handleChangeWithResetPage = (pageOne: any) => {
    setPage(1);
    handleChange(pageOne);
  };

  const refreshTable = () => {
    setPage(1);
    searchCajasCurriculares({ filtros: { ...values, page: 1 } });
  };

  const renderActions = (elemento: CajaCurricularI) => {
    return (
      <CajasCurricularesRowActions
        elemento={elemento}
        onClickEdit={handleEdit}
        onClickVerDetalle={handleVerDetalle}
        refreshTable={refreshTable}
      />
    );
  };

  useEffect(() => {
    searchCajasCurriculares({ filtros: { ...values, limit, page } });
  }, [values]);

  return (
    <section>
      <h1 className="section-title">Cajas curriculares</h1>

      <h2 className="section-subtitle mt-2">
        Desde aquí podrás gestionar las cajas curriculares.
      </h2>

      <div className="d-flex justify-content-between mt-4 mb-3">
        <div className="d-flex column-gap-3">
          <Input
            size="sm"
            id="nombre"
            onChange={handleChangeWithResetPage}
            placeholder="Buscar nombre"
            values={values}
            startIcon={<LupaIcon />}
          />
          <Button
            size="sm"
            variant="secondary"
            className="clear-filters-button"
            onClick={handleClearFilters}
            disabled={!values.nombre}
          >
            Limpiar filtros
          </Button>
        </div>
        <Button size="lg" onClick={handleCreate}>
          Nueva Caja Curricular
        </Button>
      </div>

      {isLoading && (
        <div className="d-flex justify-content-center w-full">
          <Spinner />
        </div>
      )}

      {!isLoading && elementos && cantidad != undefined && (
        <>
          <CajasCurricularesTable
            elementos={elementos}
            renderActions={renderActions}
          />
          <Pagination
            totalItems={cantidad}
            currentPage={page}
            currentLimit={limit}
            onChangePage={handleChangePagination}
            showPaginationOnlyOnMoreThanPages={1}
          />
        </>
      )}

      <CajaCurricularDetailModal
        showModal={showModalDetail}
        onClose={handleCloseModalDetail}
        elemento={selectedElement ?? null}
      />
    </section>
  );
};
